import React from 'react';
import {
  RtbCampaignSetupFlowPageProps,
  RtbCampaignSetupFlowPageState
} from './RtbCampaignSetupFlowPageModel';
import { RouteComponentProps, Redirect } from 'react-router-dom';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import NavigationPrompt from 'components/common/NavigationPrompt/NavigationPrompt';
import { StepPage, StepRenderProps } from 'containers/StepPage/StepPage';
import { ChooseAdTypeStep } from './FlowSteps/ChooseAdTypeStep';
import { RtbCampaignSetupStep } from './FlowSteps/RtbCampaignSetupStep';
import { RtbCampaignSummaryStep } from './FlowSteps/RtbCampaignSummaryStep';
import { DefaultChooseAdTypeStepModel } from './FlowSteps/ChooseAdTypeStepModel';
import { DefaultRtbCampaignSetupStepModel } from './FlowSteps/RtbCampaignSetupStepModel';
import styles from './rtbCampaignSetupFlowPage.module.scss';
import i18n from 'i18n';
import { AppFooter } from 'components/AppFooter';
import { DynamicBreadcrumb } from 'components/Breadcrumbs/DynamicBreadcrumbs';
import { TitleWithBreadcrumbs } from 'components/TitleArea/TitleWithBreadcrumbs';
import _ from 'lodash';
import { SavedTAManagement } from 'containers/Limitations/SavedTAManagement/SavedTAManagement';
import { L1ObjectObjective } from 'core/l1Object/L1Object';
import { PmpListProvider } from './contexts/PmpListProvider';
import { SelectPmpStep } from './FlowSteps/SelectPmpStep';
import {
  AbstractPmpFormModel,
  PmpCampaign,
  PmpFormModel
} from './RtbCampaignForm/PmpFormModel';
import { Pmp } from 'core/pmp/Pmp';
import { RtbCampaignBasicFormData } from 'core/rtbCampaign/RtbCampaign';
import { CreateCreativeInL2ObjectSetupFlowPageModel } from 'containers/Creatives/CreativeSetupFlow/CreativeSetupFlowPageModel';
import classNames from 'classnames/bind';
const cx = classNames.bind(styles);

export class RtbCampaignSetupFlowPage extends React.Component<
  RouteComponentProps & RtbCampaignSetupFlowPageProps,
  RtbCampaignSetupFlowPageState
> {
  handler: any;

  componentDidMount() {
    this.handler = this.props.model.event.add((model) => {
      this.setState(model.state);
    });
    this.props.model.init();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.model !== this.props.model) {
      prevProps.model.event.remove(this.handler);
      this.handler = this.props.model.event.add((model) => {
        this.setState(model.state);
      });
      this.props.model.init();
    }
  }

  componentWillUnmount() {
    this.props.model.onUnmount(this.handler);
  }

  onPromptCancel = () => {
    this.props.model.setRedirectPath(undefined);
  };

  renderTitle = () => {
    return (
      <div className={styles.topArea}>
        <TitleWithBreadcrumbs
          title={this.props.model.getTitle()}
          routes={[
            {
              path: '/orders',
              breadcrumb: i18n.t('orderDetail.labels.title')
            },
            {
              path: '/orders/:orderNumber',
              breadcrumb: DynamicBreadcrumb,
              props: {
                label: _.get(this.props.model.order, 'projectName'),
                matchParam: 'orderNumber'
              }
            },
            {
              path: '/orders/:orderNumber/campaign-groups/:l1ObjectId',
              breadcrumb: DynamicBreadcrumb,
              props: {
                label: _.get(this.props.model.l1Object, 'name'),
                matchParam: 'l1ObjectId'
              }
            },
            {
              path: '/orders/:orderNumber/campaign-groups/:l1ObjectId/campaigns/new',
              breadcrumb: this.props.model.getTitle()
            },
            {
              path: '/orders/:orderNumber/campaign-groups/:l1ObjectId/campaigns/:campaignId/edit',
              breadcrumb: DynamicBreadcrumb,
              props: {
                prefix: this.props.model.getTitle(),
                label: _.get(this.props.model.state.campaign, 'basic.name'),
                matchParam: 'campaignId'
              }
            },
            {
              path: '/orders/:orderNumber/campaign-groups/:l1ObjectId/drafts/:draftId/edit',
              breadcrumb: DynamicBreadcrumb,
              props: {
                prefix: this.props.model.getTitle(),
                label: _.get(this.props.model.state.campaign, 'basic.name'),
                matchParam: 'draftId'
              }
            }
          ]}
        />
      </div>
    );
  };

  renderAdTypeStep = ({ goNext }) => {
    return (
      <>
        {this.renderTitle()}
        <div className={styles.contentContainer}>
          <ChooseAdTypeStep
            model={new DefaultChooseAdTypeStepModel(this.props.model, goNext)}
          />
        </div>
      </>
    );
  };

  renderSelectPmpStep = ({ goNext, goLast }: StepRenderProps) => {
    if (
      !(this.props.model.campaignBasicFormModel instanceof AbstractPmpFormModel)
    ) {
      return <div />;
    }

    const pmpFormModel = this.props.model
      .campaignBasicFormModel as PmpFormModel;
    const goSummary = (
      pmp: Pmp,
      newCampaign: PmpCampaign,
      createNewCreative: boolean
    ) => {
      pmpFormModel.setPmp(pmp);
      this.props.model.setCampaign({
        basic: (this.props.model.state.campaign
          ? {
              ...this.props.model.state.campaign.basic,
              ...newCampaign.basic
            }
          : newCampaign.basic) as RtbCampaignBasicFormData,
        limitations: newCampaign.limitations
      });
      this.props.model.setCreativeSetupStepData(createNewCreative);
      goNext();
    };

    return (
      <>
        {this.renderTitle()}
        <div className={styles.contentContainer}>
          <SelectPmpStep
            pmpFormModel={this.props.model.campaignBasicFormModel}
            goNext={goSummary}
            goLast={goLast}
          />
        </div>
      </>
    );
  };

  renderMainStep = ({
    subStepIndex,
    goSubStep,
    goNext,
    goLast,
    registerValidateMethod
  }) => {
    return (
      <>
        {this.renderTitle()}
        <div className={styles.contentContainer}>
          <RtbCampaignSetupStep
            model={
              new DefaultRtbCampaignSetupStepModel(
                this.props.model,
                subStepIndex,
                goSubStep,
                goNext,
                goLast,
                registerValidateMethod
              )
            }
          />
        </div>
      </>
    );
  };

  renderSetupCreativeStep = ({ goNext, goLast }) => {
    const creativeSetupStepData = this.props.model.state.creativeSetupStepData;
    if (!creativeSetupStepData) {
      return <div />;
    }
    const { component: StepComponent, props } = creativeSetupStepData;
    if (!props.model) {
      return <div />;
    }

    const needTitle =
      props.model instanceof CreateCreativeInL2ObjectSetupFlowPageModel;
    const contentContainerClass = cx(styles.contentContainer, {
      withoutTitle: !needTitle
    });
    return (
      <>
        {needTitle && this.renderTitle()}
        <div className={contentContainerClass}>
          <StepComponent goNext={goNext} goLast={goLast} {...props} />
        </div>
      </>
    );
  };

  renderSummaryStep = ({
    subStepIndex,
    goLast,
    goStep,
    goSubStep
  }: StepRenderProps) => {
    const canCreateWithCreative =
      this.props.model.campaignBasicFormModel?.canCreateWithCreative;
    const creativeFlowModel =
      this.props.model.state.creativeSetupStepData?.props.model;
    const creativeSummaryStepModel =
      canCreateWithCreative && creativeFlowModel
        ? creativeFlowModel.getCreativeSummaryModel(goLast, goStep)
        : undefined;

    if (creativeFlowModel && creativeSummaryStepModel) {
      creativeSummaryStepModel.initContextData({
        creative: creativeFlowModel.creative,
        initCreative: creativeFlowModel.initCreative,
        advertisers: creativeFlowModel.advertisers,
        supportedCreativeType: creativeFlowModel.supportedCreativeType,
        tenmaxCategories: creativeFlowModel.tenmaxCategories,
        enableAdLogo: creativeFlowModel.enableAdLogo,
        forPmp: creativeFlowModel.forPmp,
        setCreative: creativeFlowModel.setCreative,
        setFinishedRedirectData: creativeFlowModel.setFinishedRedirectData,
        getFormContentModelOfType: creativeFlowModel.getFormContentModelOfType,
        getSummaryModel: creativeFlowModel.getSummaryModel
      });
    }

    return (
      <>
        {this.renderTitle()}
        <div className={styles.contentContainer}>
          <RtbCampaignSummaryStep
            model={this.props.model.getRtbCampaignSummaryStepModel(
              subStepIndex,
              goLast,
              goStep,
              goSubStep,
              creativeSummaryStepModel
            )}
          />
        </div>
      </>
    );
  };

  renderFlowStepPage = () => {
    const model = this.props.model;
    if (!model.campaignBasicFormModel) {
      return <LoadingIndicator />;
    }
    const canCreateWithCreative =
      model.campaignBasicFormModel.canCreateWithCreative;
    return (
      <StepPage cancel={this.props.model.cancel}>
        {model.type === 'create' && (
          <StepPage.Step
            label={i18n.t('campaign.labels.adType')}
            renderStepContent={this.renderAdTypeStep}
          />
        )}
        {model.isPmp ? (
          <StepPage.Step
            label={i18n.t('campaign.labels.pmpSelect')}
            renderStepContent={this.renderSelectPmpStep}
          />
        ) : (
          <StepPage.Step
            label={i18n.t('campaign.labels.campaignSetting')}
            renderStepContent={this.renderMainStep}
          >
            <StepPage.SubStep label={i18n.t('stepSideBar.labels.basic')} />
            <StepPage.SubStep label={i18n.t('stepSideBar.labels.targeting')} />
            {this.props.model.needSetupTracking && (
              <StepPage.SubStep
                label={i18n.t('stepSideBar.labels.strategyAndTracking')}
              />
            )}
          </StepPage.Step>
        )}
        {canCreateWithCreative && (
          <StepPage.Step
            label={i18n.t('stepSideBar.labels.creativeSetUp')}
            renderStepContent={this.renderSetupCreativeStep}
          />
        )}
        {canCreateWithCreative ? (
          <StepPage.Step
            label={i18n.t('campaign.labels.summary')}
            renderStepContent={this.renderSummaryStep}
          >
            <StepPage.SubStep
              label={i18n.t('stepSideBar.labels.campaignSettingSummary')}
            />
            <StepPage.SubStep
              label={i18n.t('stepSideBar.labels.creativeSettingSummary')}
            />
          </StepPage.Step>
        ) : (
          <StepPage.Step
            label={i18n.t('campaign.labels.summary')}
            renderStepContent={this.renderSummaryStep}
          />
        )}
        <StepPage.SideBarFooter>
          <AppFooter />
        </StepPage.SideBarFooter>
      </StepPage>
    );
  };

  renderSummaryFlowStepPage = () => {
    return (
      <StepPage cancel={this.props.model.cancel}>
        <StepPage.Step
          label={i18n.t('campaign.labels.summary')}
          renderStepContent={this.renderSummaryStep}
        />
        <StepPage.SideBarFooter>
          <AppFooter />
        </StepPage.SideBarFooter>
      </StepPage>
    );
  };

  render() {
    const model = this.props.model;
    const redirectPath = model.state.redirectPath;
    if (!model.state.campaign || !model.campaignBasicFormModel) {
      return <LoadingIndicator />;
    }
    // remove dailyTargetBudget check
    if (model.l1Object.objective === L1ObjectObjective.UNSPECIFIED) {
      return <Redirect to={`${this.props.location.pathname}/error403`} />;
    }

    return (
      <PmpListProvider l1ObjectId={model.l1Object.l1ObjectId}>
        <div className={styles.campaignSetupFlowPages}>
          {model.state.loading && <LoadingIndicator />}
          <NavigationPrompt
            when={model.type !== 'summary' && !model.state.finished}
            onCancel={this.onPromptCancel}
          />
          {model.state.showTAManagement && (
            <SavedTAManagement
              onImport={model.importLimitation}
              onDelete={model.onDeleteSaveTA}
              onClose={_.partial(model.setShowTAManagement, false)}
              getSummaryData={model.getLimitationsSummaryData}
              savedTAList={model.savedTAList}
            />
          )}
          {model.type === 'summary'
            ? this.renderSummaryFlowStepPage()
            : this.renderFlowStepPage()}
          {redirectPath && <Redirect to={redirectPath} />}
        </div>
      </PmpListProvider>
    );
  }
}
